import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Avatar,
  IconButton,
  Badge,
  Chip,
  Stack,
  Alert
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import SpeedIcon from '@mui/icons-material/Speed';
import PaymentIcon from '@mui/icons-material/Payment';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import WarningIcon from '@mui/icons-material/Warning';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import moment from 'moment';
import { fetchMeterData } from '../features/utilityMeters/utilityMeterSlice';
import { fetchNotifications } from '../features/notifications/notificationSlice';

// 元件引入
import CCTVViewer from '../components/CCTVViewer';
import UtilityMeters from '../components/UtilityMeters';
import Notifications from '../components/Notifications';
import PaymentUpload from '../components/PaymentUpload';
import OTPGenerator from '../components/OTPGenerator';
import VisitorAccess from '../components/VisitorAccess';
import ReportIssue from '../components/ReportIssue';

// 住戶儀表板首頁
const DashboardHome = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user = {}, userUnit = {} } = useSelector(state => state.auth || {});
  const { notifications, unreadCount } = useSelector(state => state.notifications || {});
  const { meters = {}, loading } = useSelector(state => state.utilityMeters || {});
  
  // 在組件掛載時獲取數據
  useEffect(() => {
    if (user) {
      // 獲取公用設施數據
      dispatch(fetchMeterData('A-1201'));
      // 獲取通知數據
      dispatch(fetchNotifications('resident'));
    }
  }, [dispatch, user]);
  
  // 過濾未讀通知
  // 確保 notifications 是一個數組並且有值，避免 TypeError: Cannot read properties of undefined (reading 'length')
  const notificationsArray = Array.isArray(notifications) ? notifications : [];
  const unreadNotifications = notificationsArray.filter(n => !n.read) || [];
  
  // 獲取公用設施儀表資料
  // 確保 meters 的子屬性是數組且有值
  const electricityArray = Array.isArray(meters?.electricity) ? meters.electricity : [];
  const waterArray = Array.isArray(meters?.water) ? meters.water : [];
  const gasArray = Array.isArray(meters?.gas) ? meters.gas : [];
  
  const electricityMeter = electricityArray.find(m => m?.unitId === 'A-1201') || null;
  const waterMeter = waterArray.find(m => m?.unitId === 'A-1201') || null;
  const gasMeter = gasArray.find(m => m?.unitId === 'A-1201') || null;
  
  // 卡片導航處理
  const handleNavigate = (path) => {
    navigate(path);
  };
  
  // 獲取用戶單元的儀表讀數
  const getUserMeterReadings = () => {
    if (!meters || !user || !user.unit) return null;
    
    const unitMeters = meters.filter(meter => meter.unit === user.unit);
    const latestReadings = {};
    
    unitMeters.forEach(meter => {
      if (!latestReadings[meter.type] || new Date(meter.timestamp) > new Date(latestReadings[meter.type].timestamp)) {
        latestReadings[meter.type] = meter;
      }
    });
    
    return latestReadings;
  };
  
  const meterReadings = getUserMeterReadings() || {};
  
  // 快速訪問卡片
  const quickAccessCards = [
    {
      title: 'CCTV 監控',
      icon: <VideoCallIcon sx={{ fontSize: 40 }} color="primary" />,
      description: '查看樓宇的安全監控攝像頭',
      path: '/resident/cctv',
      color: 'primary.light'
    },
    {
      title: '用電/水/氣讀數',
      icon: <SpeedIcon sx={{ fontSize: 40 }} color="info" />,
      description: '查看和管理公用設施使用情況',
      path: '/resident/meters',
      color: 'info.light'
    },
    {
      title: '訪客通行證',
      icon: <VpnKeyIcon sx={{ fontSize: 40 }} color="success" />,
      description: '為訪客生成臨時通行證',
      path: '/resident/visitor-access',
      color: 'success.light'
    },
    {
      title: '通知中心',
      icon: <NotificationsIcon sx={{ fontSize: 40 }} color="warning" />,
      description: `${unreadCount} 條未讀通知`,
      path: '/resident/notifications',
      color: 'warning.light'
    },
    {
      title: '付款上傳',
      icon: <PaymentIcon sx={{ fontSize: 40 }} color="secondary" />,
      description: '上傳管理費和公用設施付款憑證',
      path: '/resident/payments',
      color: 'secondary.light'
    },
    {
      title: '通告查看',
      icon: <AnnouncementIcon sx={{ fontSize: 40 }} color="info" />,
      description: '查看物業管理處發布的通告',
      path: '/resident/announcements',
      color: 'info.light'
    },
    {
      title: '問題報告',
      icon: <ReportProblemIcon sx={{ fontSize: 40 }} color="error" />,
      description: '報告維修問題或物業投訴',
      path: '/resident/issues',
      color: 'error.light'
    }
  ];
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        歡迎, {user?.name || '住戶'}
      </Typography>
      
      <Typography variant="subtitle1" color="text.secondary" paragraph>
        單位: {userUnit?.number || '-'} | 今天是 {new Date().toLocaleDateString('zh-TW', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
      </Typography>
      
      <Grid container spacing={3}>
        {/* 快速訪問卡片 */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            快速訪問
          </Typography>
          
          <Grid container spacing={2}>
            {quickAccessCards.map((card, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 100,
                      height: 100,
                      background: `radial-gradient(circle at 100% 0%, ${card.color} 0%, transparent 70%)`,
                      opacity: 0.2
                    }}
                  />
                  <CardActions>
                    <Button 
                      size="small" 
                      endIcon={<ArrowForwardIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNavigate(card.path);
                      }}
                    >
                      {card.title}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
        
        {/* 當前狀態概覽 */}
        <Grid item xs={12} md={8}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            當前狀態
          </Typography>
          
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={3}>
              {/* 電力使用情況 */}
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'warning.light', mr: 2 }}>
                    <ElectricBoltIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      電力用量
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="h6">
                        {meterReadings.electricity?.value || '-'} kWh
                      </Typography>
                      {meterReadings.electricity?.trend === 'up' ? (
                        <TrendingUpIcon color="error" fontSize="small" sx={{ ml: 1 }} />
                      ) : (
                        <TrendingDownIcon color="success" fontSize="small" sx={{ ml: 1 }} />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              {/* 水資源使用情況 */}
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'info.light', mr: 2 }}>
                    <WaterDropIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      水資源用量
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="h6">
                        {meterReadings.water?.value || '-'} m³
                      </Typography>
                      {meterReadings.water?.trend === 'up' ? (
                        <TrendingUpIcon color="error" fontSize="small" sx={{ ml: 1 }} />
                      ) : (
                        <TrendingDownIcon color="success" fontSize="small" sx={{ ml: 1 }} />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              {/* 燃氣使用情況 */}
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'error.light', mr: 2 }}>
                    <LocalFireDepartmentIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      燃氣用量
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="h6">
                        {meterReadings.gas?.value || '-'} m³
                      </Typography>
                      {meterReadings.gas?.trend === 'up' ? (
                        <TrendingUpIcon color="error" fontSize="small" sx={{ ml: 1 }} />
                      ) : (
                        <TrendingDownIcon color="success" fontSize="small" sx={{ ml: 1 }} />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* 通知部分 */}
        <Grid item xs={12} md={4}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            通知
          </Typography>
          
          <Paper sx={{ p: 2 }}>
            {unreadNotifications.length > 0 ? (
              <List>
                {unreadNotifications.map((notification, index) => (
                  <React.Fragment key={notification.id}>
                    <ListItem alignItems="flex-start" button onClick={() => handleNavigate('/resident/notifications')}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: notification.type === 'warning' ? 'error.main' : 'primary.main' }}>
                          {notification.type === 'warning' ? <WarningIcon /> : <NotificationsIcon />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={notification.title}
                        secondary={notification.content}
                        primaryTypographyProps={{ noWrap: true }}
                        secondaryTypographyProps={{ 
                          noWrap: true,
                          sx: { display: 'block' } 
                        }}
                      />
                    </ListItem>
                    {index < unreadNotifications.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
                
                {unreadCount > 3 && (
                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Button 
                      endIcon={<ArrowForwardIcon />}
                      size="small"
                      onClick={() => handleNavigate('/resident/notifications')}
                    >
                      查看全部 {unreadCount} 條未讀通知
                    </Button>
                  </Box>
                )}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  沒有未讀通知
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

// 主儀表板組件，處理路由
const ResidentDashboard = () => {
  return (
    <Routes>
      <Route path="/" element={<DashboardHome />} />
      <Route path="/cctv" element={<CCTVViewer />} />
      <Route path="/meters" element={<UtilityMeters />} />
      <Route path="/notifications" element={<Notifications />} />
      <Route path="/payments" element={<PaymentUpload />} />
      <Route path="/visitor-access" element={<OTPGenerator />} />
      <Route path="/issues" element={<ReportIssue />} />
      <Route path="/announcements" element={<Box sx={{ p: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          物業通告
        </Typography>
        <Notifications />
      </Box>} />
    </Routes>
  );
};

export default ResidentDashboard; 